import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, LoginCredentials, RegisterCredentials, AuthResponse, ChangePasswordCredentials } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<AuthResponse>;
  register: (credentials: RegisterCredentials) => Promise<AuthResponse>;
  logout: () => void;
  updateUser: (user: User) => void;
  changePassword: (credentials: ChangePasswordCredentials) => Promise<{ message: string }>;
  updatePreferences: (preferences: any) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = authService.getToken();
      const storedUser = authService.getCurrentUser();

      if (token && storedUser) {
        // Direkt storedUser'ı kullan, server'a sorgu yapma
        setUser(storedUser);
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const response = await authService.login(credentials);
      // localStorage'daki eski veriyi temizle ve yeniden yaz
      localStorage.removeItem('user');
      localStorage.setItem('user', JSON.stringify(response.user));

      // Oturum başlangıç zamanını kaydet
      const sessionStart = new Date().toISOString();
      localStorage.setItem('sessionStart', Date.now().toString());
      localStorage.removeItem('sessionEnd');

      // Backend'e oturum başlangıcını bildir
      fetch('/api/user-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${response.token}` },
        body: JSON.stringify({ type: 'start', timestamp: sessionStart })
      }).catch(() => {});

      setUser(response.user);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const register = async (credentials: RegisterCredentials): Promise<AuthResponse> => {
    try {
      const response = await authService.register(credentials);
      setUser(response.user);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Oturum bitiş zamanını kaydet
    const sessionEnd = new Date().toISOString();
    localStorage.setItem('sessionEnd', Date.now().toString());

    // Backend'e oturum bitişini bildir
    const token = authService.getToken();
    if (token) {
      fetch('/api/user-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type: 'end', timestamp: sessionEnd })
      }).catch(() => {});
    }

    // Tüm kullanıcıya özel localStorage verilerini temizle
    localStorage.removeItem('user');
    // Tüm kullanıcı portföylerini temizle
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('portfolio_')) localStorage.removeItem(key);
      if (key.startsWith('portfolio_newItem_')) localStorage.removeItem(key);
      if (key.startsWith('portfolio_sellItem_')) localStorage.removeItem(key);
      if (key.startsWith('portfolio_showAddForm_')) localStorage.removeItem(key);
      if (key.startsWith('portfolio_showSellForm_')) localStorage.removeItem(key);
    });
    // Gerekirse diğer kullanıcıya özel anahtarlar da eklenebilir
    authService.logout();
    setUser(null);
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const changePassword = async (credentials: ChangePasswordCredentials): Promise<{ message: string }> => {
    try {
      const response = await authService.changePassword(credentials);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updatePreferences = async (preferences: any) => {
    try {
      const updatedPreferences = await authService.updatePreferences(preferences);
      
      // Update user in state with new preferences
      if (user) {
        const updatedUser = { ...user, preferences: updatedPreferences };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      return updatedPreferences;
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    register,
    logout,
    updateUser,
    changePassword,
    updatePreferences,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
